import moment from 'moment-timezone';
import type { PatientLMP } from 'generated/api';
import CalendarUtils from 'services/CalendarUtils';

// The menstrual tracker stores each day at its local-midnight unix, so we
// render the LMP in the clinic (tenant) timezone to avoid a near-midnight
// off-by-one on the displayed calendar date.

export type LmpDisplay = {
  /** The headline value shown inline in the narrow CPP Medical Info box: the
   * LMP date (or 'N/A'). Kept short so the row never overflows the panel. */
  inline: string;
  /**
   * Secondary qualifiers surfaced only in the hover tooltip (so they don't widen
   * the inline row). Always leads with "Patient-tracked" so a clinician never
   * mistakes the self-reported estimate for a charted LMP. Empty when there is
   * nothing beyond the date to show (i.e. the 'N/A' case).
   */
  details: string[];
};

/**
 * Splits the patient-tracked LMP into the headline date (shown inline) and the
 * qualifying details (shown on hover). Returns inline: 'N/A' with no details
 * when there is no qualifying tracked period (including the slice-empty /
 * not-yet-loaded case, where `lmp` is undefined).
 */
export function formatLmpDisplay(lmp: PatientLMP | undefined): LmpDisplay {
  if (!lmp?.has_data || !lmp.lmp) return { inline: 'N/A', details: [] };

  const inline = moment.unix(lmp.lmp).tz(CalendarUtils.getTargetTimezone()).format('MMM D, YYYY');
  const details = ['Patient-tracked'];

  if (lmp.last_tracked_date) {
    const daysAgo = moment().diff(moment.unix(lmp.last_tracked_date), 'days');
    details.push(`Last entry ${daysAgo}d ago`);
  }

  return { inline, details };
}
